import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { PerformanceResultUserFragment } from '../../PerformanceResult.api';
import type { AssessmentRunOption } from '../../shared/utils';
import FinSwimInnerForm from '../FinSwimInnerForm';
import {
    createFinSwimPerformanceFormSchema,
    finSwimPerformanceDefaultFormValues,
    type CreateFinSwimPerformanceFormData,
} from '../FinSwimPerformanceFormConfig';
import {
    SubmitPerformanceFinSwimResultDocument,
    type SubmitPerformanceFinSwimResultMutation,
} from './CreatePerformanceFinSwimResult.api';
import client from '@/apolloClient';
import { UserRole, type SubmitFinSwimResultInput } from '@/apolloClient.types';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import DatePickerField from '@/components/fields/DatePickerField';
import SelectField from '@/components/fields/SelectField';
import SwitchField from '@/components/fields/SwitchField';
import { useActiveRole, useGetCompanyId, useUser } from '@/contexts/UserSession';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';
import '@/utils/notifications';
import useCompanyRoleLabels from '@/utils/useCompanyRoleLabels';

interface CreatePerformanceFinSwimModalProps {
    open: boolean;
    onClose: (refresh?: boolean) => void;
    groupUsers: PerformanceResultUserFragment[];
    assessmentRunOptions: AssessmentRunOption[];
}

const CreatePerformanceFinSwimModal = ({
    open,
    onClose,
    groupUsers,
    assessmentRunOptions,
}: CreatePerformanceFinSwimModalProps) => {
    const { t } = useTranslation('performanceResult');
    const selectedCompanyId = useGetCompanyId();
    const { getRoleLabel } = useCompanyRoleLabels();
    const user = useUser(true);
    const activeRole = useActiveRole();

    const isTrainee = useMemo(() => {
        return activeRole && activeRole.role === UserRole.Trainee;
    }, [activeRole]);

    const form = useForm<CreateFinSwimPerformanceFormData>({
        mode: 'onChange',
        resolver: zodResolver(createFinSwimPerformanceFormSchema),
        defaultValues: { ...finSwimPerformanceDefaultFormValues, userId: isTrainee ? user.id : undefined },
    });

    const isTiedToAssessmentRun = form.watch('isTiedToAssessmentRun');

    useEffect(() => {
        if (!isTiedToAssessmentRun) {
            form.setValue('assessmentRunId', null);
        }
    }, [form, isTiedToAssessmentRun]);

    const handleSubmit = useCallback(async () => {
        if (!(await form.trigger())) {
            return;
        }

        const values = form.getValues();

        const request: SubmitFinSwimResultInput = {
            assessmentRunId: values.isTiedToAssessmentRun ? values.assessmentRunId || undefined : undefined,
            userId: values.userId,
            companyId: selectedCompanyId || '',
            completedAt: values.date,
            result: {
                timingSec: values.completionTime ?? undefined,
            },
        };

        try {
            const { data } = await client.mutate<SubmitPerformanceFinSwimResultMutation>({
                mutation: SubmitPerformanceFinSwimResultDocument,
                variables: {
                    input: request,
                },
            });

            if (data?.submitFinSwimResult?.__typename === 'ErrorResponse') {
                const errorResponse = data.submitFinSwimResult;
                const specificErrorField = errorResponse.fields?.find(f => f.message);
                const errorMessage = specificErrorField?.message || t('createError.description', { type: 'FIN Swim' });

                showErrorNotification({
                    message: t('createError.title', { type: 'FIN Swim' }),
                    description: errorMessage,
                });
                throw new Error(errorMessage);
            } else {
                showSuccessNotification({
                    message: t('createSuccess.title', { type: 'FIN Swim' }),
                    description: t('createSuccess.message', { type: 'FIN Swim' }),
                });

                onClose(true);
            }
        } catch (error) {
            console.error('Error submitting FIN Swim performance result:', error);
            throw error;
        }
    }, [form, onClose, selectedCompanyId, t]);

    const userOptions = useMemo(() => {
        const options = [];
        for (const user of groupUsers) {
            if (user.__typename === 'TenantUser') {
                options.push({ label: user.name, value: user.id });
            }
        }
        return options;
    }, [groupUsers]);

    const modalFooter = (
        <div className="mt-5 flex w-full justify-end">
            <div className="flex space-x-2">
                <Button outline onClick={() => onClose()}>
                    {t('finSwim.createModal.cancelBtn')}
                </Button>
                <Button color="teal" onClick={handleSubmit}>
                    {t('finSwim.createModal.saveBtn')}
                </Button>
            </div>
        </div>
    );

    return (
        <FormProvider {...form}>
            <Modal
                open={open}
                onClose={onClose}
                title={t('finSwim.createModal.title')}
                footer={modalFooter}
                showCloseButton={false}
                className="w-full sm:w-xl"
                largeTitle
            >
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 sm:grid-cols-3">
                        <div className="sm:col-span-3">
                            <SelectField
                                name="userId"
                                label={t('finSwim.form.traineeName', { role: getRoleLabel(UserRole.Trainee) })}
                                placeholder={t('finSwim.form.traineeNamePlaceholder', {
                                    role: getRoleLabel(UserRole.Trainee).toLocaleLowerCase(),
                                })}
                                searchable
                                options={userOptions}
                                required
                                disabled={!!isTrainee}
                            />
                        </div>
                        <div className="sm:col-span-3">
                            <DatePickerField name="date" label={t('finSwim.form.date')} required />
                        </div>
                        <FinSwimInnerForm />
                        <div className="sm:col-span-3">
                            <SwitchField
                                name="isTiedToAssessmentRun"
                                label={t('soc.form.tieToAssessmentRun', {
                                    defaultValue: 'Tie To Assessment Run',
                                })}
                            />
                        </div>
                        {isTiedToAssessmentRun && (
                            <div className="sm:col-span-3">
                                <SelectField
                                    name="assessmentRunId"
                                    label={t('filter.assessmentRun')}
                                    placeholder={t('filter.assessmentRunPlaceholder')}
                                    options={assessmentRunOptions}
                                    required
                                />
                            </div>
                        )}
                    </div>
                </form>
            </Modal>
        </FormProvider>
    );
};

export default CreatePerformanceFinSwimModal;
